import de from './locales/de.json'
import en from './locales/en.json'
import fr from './locales/fr.json'
import it from './locales/it.json'

/** Flat message catalogs for the search package (`deep_search.*` keys). */
const SEARCH_I18N_MESSAGES = {
  en,
  de,
  fr,
  it,
} as const

export type SearchLocale = keyof typeof SEARCH_I18N_MESSAGES

/** Minimal vue-i18n surface needed by the search package (avoids host catalog generics). */
export interface SearchI18nHost {
  global: {
    mergeLocaleMessage: (locale: string, message: unknown) => void
    t: (key: string, ...args: unknown[]) => string
  }
}

let installedI18n: SearchI18nHost | undefined

/**
 * Register search copy on the host vue-i18n instance.
 * Call once after `createI18n` / `app.use(i18n)`.
 */
export function installSearchI18n(i18n: SearchI18nHost): void {
  for (const locale of Object.keys(SEARCH_I18N_MESSAGES) as SearchLocale[]) {
    i18n.global.mergeLocaleMessage(locale, SEARCH_I18N_MESSAGES[locale])
  }
  installedI18n = i18n
}

/** Host vue-i18n instance after {@link installSearchI18n}. */
function getSearchI18n(): SearchI18nHost {
  if (!installedI18n) {
    throw new Error(
      '[search] installSearchI18n(i18n) must run before using search translations',
    )
  }
  return installedI18n
}

/**
 * vue-i18n `global.t` for search TS modules outside `<script setup>`.
 * Requires {@link installSearchI18n} first.
 */
export function t(key: string, ...args: unknown[]): string {
  return getSearchI18n().global.t(key, ...args)
}

function interpolate(
  template: string,
  named: Record<string, unknown> | undefined,
): string {
  if (!named) {
    return template
  }
  return template.replaceAll(/\{(\w+)\}/g, (_, name: string) => {
    const value = named[name]
    return value == null ? `{${name}}` : String(value)
  })
}

function catalogMessage(locale: SearchLocale, key: string): string | undefined {
  const table = SEARCH_I18N_MESSAGES[locale] as Record<string, string>
  const en = SEARCH_I18N_MESSAGES.en as Record<string, string>
  return table[key] ?? en[key]
}

/**
 * Resolve a search message for an explicit locale (field labels, descriptions, etc.).
 * Prefers the installed host i18n (so merge overrides apply), then package catalogs.
 */
export function translateForLocale(
  key: string,
  locale: SearchLocale,
  named?: Record<string, unknown>,
): string {
  if (installedI18n) {
    const translated = installedI18n.global.t(key, named ?? {}, { locale })
    if (typeof translated === 'string' && translated !== key) {
      return translated
    }
  }

  const fromCatalog = catalogMessage(locale, key)
  if (fromCatalog) {
    return interpolate(fromCatalog, named)
  }
  return key
}
